import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AlarmService} from '../alarm.service';
import {addDays, subDays, format} from 'date-fns/esm';
import {groupBy} from 'lodash-es';
import {BackendAlarm} from '../alarm-definitions/backend-alarm';
import {parseToDate} from '../../shared/utils';
import {BaseComponent} from '../../components/general/base/base.component';

@Component({
  selector: 'qm-alarm-diagram',
  templateUrl: './alarm-diagram.component.html',
  styleUrls: ['./alarm-diagram.component.scss']
})
export class AlarmDiagramComponent extends BaseComponent implements OnInit {

  public alarms: { name: string, value: number }[] = [];
  public dateArr: Date[] = [];
  public selected: boolean = false;
  public list: { name: string, amount: number }[] = [];
  public grouped: Map<string, BackendAlarm[]> = new Map<string, BackendAlarm[]>();
  public activeAlarm: string = '';
  public show: boolean = false;
  public alarmDetails: any[];
  public loading = false;

  constructor(private alarmService: AlarmService, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    const from = subDays(new Date(), 14);
    const to = new Date();
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    this.dateArrayPrev(to);
    // Nice syntax trick to allow await without async using inline function
    (async () => {
      this.modifyResults(await this.loadPaginatedResults(from, to));
    })();
  }

  private async loadPaginatedResults(from: Date, to: Date) {
    let results: BackendAlarm[] = [];
    let page = 0;
    const pageSize = 3000;
    const query = 'rank=1&activatedAt<=' + to.toISOString() + '&activatedAt>=' + from.toISOString() + '&sort=activatedAt&limit=' + pageSize;
    this.loading = true;
    while (this.loading) {
      let q = query;
      if (page) {
        q += '&offset=' + page * pageSize;
      }
      const r = await this.alarmService.queryAlarms(q);
      if (r.isOk) {
        results = results.concat(r.data.data);
        page++;
        if (r.data.data.length !== pageSize) {
          this.loading = false;
          break;
        }
      } else {
        this.loading = false;
      }
    }
    return results;
  }

  public showTargets(key: string): { name: string, amount: number }[] {
    const messages = groupBy(this.grouped.get(key).map(o => {
      return this.alarmService.getTranslatedAlarm(o, false);
    }));
    const result: { name: string, amount: number }[] = [];
    Object.keys(messages).forEach((o: string) => {
      result.push({name: o, amount: messages[o].length});
    });
    return result;
  }

  public setActive(name: string) {
    if (name === this.activeAlarm) {
      this.activeAlarm = '';
    } else {
      this.activeAlarm = name;
    }
    this.alarmDetails = this.showTargets(name);
  }

  public isActive(name: string) {
    if (this.activeAlarm === name) {
      return true;
    }
    return false;
  }

  public async selectBar(value: any) {
    const dateArray = value.name.split('.');
    const from = new Date();
    from.setFullYear(parseInt(dateArray[2]), parseInt(dateArray[1]) - 1, parseInt(dateArray[0]));
    const to = new Date(from);
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    this.topList(await this.loadPaginatedResults(from, to));
  }

  async loadMore(direction: string) {
    this.alarms = [];
    switch (direction) {
      case 'next':
        this.dateArrayNext(this.dateArr[13]);
        this.modifyResults(await this.loadPaginatedResults(this.dateArr[13], this.dateArr[0]));
        break;
      case 'previous':
        this.dateArrayPrev(this.dateArr[0]);
        this.modifyResults(await this.loadPaginatedResults(this.dateArr[13], this.dateArr[0]));
        break;
    }
  }

  public dateArrayPrev(date: Date): void {
    const startD = date;
    startD.setHours(23, 59, 59, 999);
    this.dateArr = [startD];
    for (let i: number = 1; i < 14; i++) {
      const temp1: Date = subDays(startD, i);
      temp1.setHours(0, 0, 0, 0);
      this.dateArr.push(temp1);
    }
  }

  private dateArrayNext(date: Date): void {
    const startD = addDays(date, 13);
    startD.setHours(23, 59, 59, 999);
    this.dateArr = [startD];
    for (let i: number = 1; i < 14; i++) {
      const temp1: Date = subDays(startD, i);
      temp1.setHours(0, 0, 0, 0);
      this.dateArr.push(temp1);
    }
  }

  private modifyResults(results: BackendAlarm[]) {
    const groupedAlarms = groupBy(results, (o: BackendAlarm) => {
      return format(parseToDate(o.createdAt), 'dd.MM.yyyy');
    });
    this.dateArr.reverse().forEach(entry => {
      const ds = format(entry, 'dd.MM.yyyy');
      const alarms = groupedAlarms[ds];
      this.alarms.push({name: ds, value: alarms ? alarms.length : 0});
    });
    this.changeDetector.markForCheck();
  }

  private topList(results: BackendAlarm[]) {
    this.list = [];
    const hmm = groupBy(results, this.getGroupProperty(results[0].type));
    const keys = Object.keys(hmm);
    keys.forEach((o: string) => {
      this.list.push({name: o, amount: hmm[o].length});
      this.grouped.set(o, hmm[o]);
    });
    this.list.sort((a, b) => {
      return a.amount - b.amount;
    });
    this.selected = true;
  }

  private getGroupProperty(type: string) {
    if (type === 'miproMeasure') {
      return 'parentId';
    } else {
      return 'alarmId';
    }
  }
}
